import { lazy, Suspense, useEffect, useState, type CSSProperties } from 'react';
import { NavLink, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { emailInitials } from '@/modules/lab/utils';
import { AboutModal } from '@/components/AboutModal';
import { ChangePasswordModal } from '@/modules/auth/ChangePasswordModal';
import { LoginPage } from '@/modules/auth/LoginPage';
import { PendingApprovalPage } from '@/modules/auth/PendingApprovalPage';
// ── Route-level code splitting ──────────────────────────────────────────────
// Every module page is loaded on demand. Previously all 16 pages were imported
// eagerly, so each visitor downloaded the whole portal (PMRD Maker, SheetJS,
// the EP/Cert/Samples modules…) before the login screen could render. Now the
// startup bundle holds only the shell + login, and each module arrives the
// first time someone opens it (then stays cached by the browser).
//
// The pages use NAMED exports, so each import maps the named export onto
// `default`, which is what React.lazy requires.
const HomePage = lazy(() => import('@/modules/home/HomePage').then((m) => ({ default: m.HomePage })));
const LabPage = lazy(() => import('@/modules/lab/LabPage').then((m) => ({ default: m.LabPage })));
const EPPage = lazy(() => import('@/modules/ep/EPPage').then((m) => ({ default: m.EPPage })));
const CertPage = lazy(() => import('@/modules/cert/CertPage').then((m) => ({ default: m.CertPage })));
const SamplesPage = lazy(() => import('@/modules/samples/SamplesPage').then((m) => ({ default: m.SamplesPage })));
// Phone scanner: full screen (outside the sidebar shell) so it fits a phone.
const SampleScanPage = lazy(() => import('@/modules/samples/SampleScanPage').then((m) => ({ default: m.SampleScanPage })));
const SampleReviewPage = lazy(() => import('@/modules/sample-review/SampleReviewPage').then((m) => ({ default: m.SampleReviewPage })));
const TrainingPage = lazy(() => import('@/modules/training/TrainingPage').then((m) => ({ default: m.TrainingPage })));
const UsersPage = lazy(() => import('@/modules/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const ApprovalsPage = lazy(() => import('@/modules/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })));
const EngineeringReportsPage = lazy(() => import('@/modules/eng-reports/EngineeringReportsPage').then((m) => ({ default: m.EngineeringReportsPage })));
const CertLabelsPage = lazy(() => import('@/modules/cert-labels/CertLabelsPage').then((m) => ({ default: m.CertLabelsPage })));
const RequestTestPage = lazy(() => import('@/modules/test-requests/RequestTestPage').then((m) => ({ default: m.RequestTestPage })));
const EquipmentPage = lazy(() => import('@/modules/misc/MiscPages').then((m) => ({ default: m.EquipmentPage })));
const QualitySystemPage = lazy(() => import('@/modules/misc/MiscPages').then((m) => ({ default: m.QualitySystemPage })));
const PMRDPage = lazy(() => import('@/modules/misc/MiscPages').then((m) => ({ default: m.PMRDPage })));

// Warm EVERY module in the background once the app is idle, so the first
// click on any page is instant instead of waiting on a download. Most-used
// pages first; the rest follow one by one so the browser never stalls.
function prefetchCommonModules() {
  const loaders: (() => Promise<unknown>)[] = [
    () => import('@/modules/lab/LabPage'),
    () => import('@/modules/test-requests/RequestTestPage'),
    () => import('@/modules/approvals/ApprovalsPage'),
    () => import('@/modules/ep/EPPage'),
    () => import('@/modules/cert/CertPage'),
    () => import('@/modules/misc/MiscPages'),
    () => import('@/modules/sample-review/SampleReviewPage'),
    () => import('@/modules/samples/SamplesPage'),
    () => import('@/modules/eng-reports/EngineeringReportsPage'),
    () => import('@/modules/cert-labels/CertLabelsPage'),
    () => import('@/modules/training/TrainingPage'),
    () => import('@/modules/users/UsersPage'),
  ];
  const w = window as unknown as { requestIdleCallback?: (cb: () => void) => void };
  const whenIdle = (cb: () => void) => {
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(cb);
    else window.setTimeout(cb, 300);
  };
  const next = (i: number) => {
    if (i >= loaders.length) return;
    whenIdle(() => { void loaders[i]().catch(() => undefined).finally(() => next(i + 1)); });
  };
  next(0);
}

/** Shown in the content area (sidebar stays put) while a module chunk loads. */
function PageLoading() {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
      Loading…
    </div>
  );
}
import './styles/liteline-theme.css';

// Auto sign-out after this much inactivity.
const IDLE_MS = 2 * 60 * 60 * 1000; // 2 hours

// Actual Liteline logo (full lock-up) shown in the sidebar, on a white chip.
const LOGO_HREF = 'data:image/png;base64,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';
// Liteline marque (square) used as the browser-tab favicon.
const FAVICON_HREF = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAALEklEQVR4nO2aXawkR3XHf6eqeubO3I/dtffDd3dt1hvMBrCxWT6ijcWnvEgmDwl5skFARHgJCCSEgZcgr6VISCDzAMqDHyKErCiKIBJBGCyTxBEW4ERYhhibbzbKGjvBYO/er/norjo8VPfM9EzP9Oxyb+5i/JdG96q7TtWp06dOnf+pgt9zCNxpLkrizjPomfKjM2fgrrvOVDQelQM9M7uNyBm4E7ir/FzHB9xGSALoRQhkU54nNXIeCDVtHCBjz3TGmNsBufvff/rNLHgMEAIYE7XIvGBQjInPnRX6/cCrj+7hlpddNZiMAf7j3HP8589+hUsgjFpTDKqABpYbCbe96mqazlYqcmGzxz88+iReIGigcEunhrefPMryYnNHDOA+fN+P/xivk6YfhzWw1uUDb76OW152FaPr5p9/ssYnvvgErDTBj37nvNMscHxvi3f90bGp3fe84wP/9EOyhs2tqCDCQhZ4203XsHxp86uFk5B6gq9viJCFHlm/A0DmAwok1tDb2kSyDi6FLJQd3Rgh+IDJhPWtHivt5tDTGHrd5vo6Lu3iMYgIqIIV0m7KxtoGB5f3EULAmIsLWbXzUlWLUusBqqAjjUTKAoqg6EQ8UR0+LWRKopIPLoICKnEwMULY6PMnJ49y+NAKQXXbJw+w/T1eKiR6ghGwRiD1vGj/Eve+41UsOIvUrtFLw2VjAAU6WSBkgSwoYaPPB99wnH2LTbKgyM7MH7cz3V48EmO48YpF+hZC6nFLK/zpK69GNXrFTmFbDCBjv2nvqmDyT3vkwBLf/vjpGDOCYq1hoZnkfeycBbbFAIEYvDLGkioBn+vua+ZgjNBqltMpRXd08rBNBlhQpZ0FGqpko5mQj4mU98pCPxBqck7V8vvxnWYnML8BKnSxeQj90OuP864br8IaQ8kH8h1OFaw1LBYuPWVe/x8THocTIUaZmrFFBGPLShbueWBPiwN7Wjuo5s7B6Wa/nMBPMURqBS706XULajLMnoIqOgejsjXhPGjR57AzQS56C4zTUUTqI4g88NhTOk//IkKWeo5c2eb6q/ehOt2Vdwv5vC9KL1nrp1oKXDM6N0ZoGEPbmtKb9Z7nfC9DNVBFaEUEJ4ZDS43BtjeOzAee3uiBxC9YrEwnwv52A2en52yKEsLQw84+18H7wIHFBntaycxM361+/Kt0s6LF9KbWCH4r5X03H+Mzt53Eh4AiOCN84t9+yt1fewK32CDz5eUkgPrAdSsLfPsjb2K51Sh5T1DFiPDkMxu84lMP0nNmsAKsFfZ55eE73sjVB5dR1cpAKQjWQAiBv/7SY9z9rbOErYx7b7+J2173YkLQqcvPbfb8GImf6D1a2Qihk5KmofwC6HlPf7NH5gxhlA4Lcaapp9OcveFoULqdlLSRGyBnhJoFhgFmchIKXNjq8+AP/pdP/suPefjsc5jFhNDNUF/v2U6sAcJ0Hym+hhHUCVWETEQQZ7CmzBjJ09hgBGvrF6a1QmaKwBXZoZtBVzRv9e57HubLjz8FCw6zmGBE0Dl2NgCnCjX5SRxMC0o8450ysRsU3c+zSww6V3KaTPw2A7evXqJPbvWwLYdtOlKvYOebE1xGZAjiMlMjjMZSra6gldBwJtYcFVQEb8xccnAZGUAV2Ewx3pS/XpCRFLn89SUX1G5K0vFYH4OqdUK6maFpXRn2MjBAsS2u7l/kwTveMBFjVOHAvkWgen9XMfzdX7yGNPUYI4M8oJ8Gjh5cimPMSMC2xQDWCtYZrDWolIuiBhCrtUGw1XSceulVFz22AC9/0ZW1baZhWwyw1cnwF7r4oDCWB/hYY+e8sTN3WwAftCKNmjeFnsQ8afRvZYBCr3fctMrLlxMsWlJGjCAiBK8stBs0EltoVom6iU7X49JzcmeNICrD2UyxprOxzeh6KgY+dXw/p47vn3vQy4lCOL/Vh9QPtaryQSAzBta7bHbSiU6Caq17C5f+hXcS7oM3Xzugj8BUKmVEyHoZrztxcKKZIBiZbYGqXmOCVJ+xVLn4tHVfJzehl86jwfMY7tZ7vonJQnn/LR1wxj/WCOtbKbdef5g7Tp8YFEGsET770M/4yqPnWFpI4k4wIisiZD6wt93knttP0m44fM7OHvr5r/ib+x5nqenKciPjpmngnttfydErlwlBMUZ4Zr3LX/79I0OvG6vCOStc2Ez52C0nOH396kCu0gD3/9fT8UAzTyJiL6OTyAWNwHqXY1csDpoUrR578jwPPPILWGqMbYNFSTiwuq9FljPFosX/ne8M5bKxrK1QOPX033ZDSS7tpNz33acIA4owZjxn4NkO775hFa5fnUkLnG034gnlLAg4I2RBaSWTSXar4bDtBq7dYLy4YkQImWd5IZmIA4kVbDvBthP8GHUVYqm91TfIWHZojLCnlbBmxgtoEdYafN+TJPUHX26QvNQVRYmJSlXwCaoxiQk64coqSgjxNw7V2CcVcoUB/JQQ5YPiqTYAEvubJ7rNfzb4PA2Vl83h6G7h994A20KGjAjWxN/4SjESOYGtKmaanEWKTHyKIgZMyx7jeFOCoIlp+zwUYVsM0Ekz/FYfb2V4vwdAJR6KZoG1hptQNPUBv9mPO2cRsQZZaazpbWRhssymyoVORpAieBd7cpTxRqCTxfJYDX4rAxQWPnlkL2+96QgLi3kipDqoAzqBvlcOtJuDKy7Fhzmy0uKtNx6mueDirbNRQ4gQVHEeWo3ymWKjmfDnr1ilL/nFidGkxAhGIN1IWb2yXZKrnIP58JdU/YyqcA5nhGyjz1+9/jh/+/ZXD7K533W44EPMwmoWTGqBLOArLpSVvHcExfqU/G0cojzO8ONV5N95kb2qKjytQl20keJv3Yc9vJjg0/prcjYx9DSw0oyZYNB4mFEeeBYEl7t1GF3n1YX24X+qJNaiqmNJUc14Gu8m1B2Pig++8kSvSqcgseRuxnPT32G4z3/rv+e6KGpE2Ew9J1aXOX3iEF/5/tOcfWadhrUVtwOHyDcBmk3Le08d4+s//zX/+pNnaCYWG6ZLikBA0KB89OZj/LLv+cIj59jXcFPT4wJWhLVexlv+8BA3HN078yTbvefe7wwZ3KzjMSew1uOdb34Jp08c4q6v/4jvPPoLWBy/IFwxk9Rz8IpF3nvqWu5//Gk+/Y/fgz0LkwywJEdc6D3P+08e5uyvO3zs3kegldSTN2fhuQ6fe89rueHo3nhWMMUCzqwsIMWBZkWQKtaaM0IGLC3EnbPdSrArTWy7McnlB6KRh4fUs68dt7KlxOKWGrjFZII5luavoAaaXY8RoZEY7FIDaSU1JEcx1hAyT6sxBxsME2ywoneJCvkwLEWFoPgsPpxqAAENsW3RwgclUyBo+Sh9UhRFsCGG+4I5Gl/NSMsI+Gy2YxYwc7G8S2WCOvaD/Mh8zg7H97q59Zg/P3n+hPNLxAsG2G0FdhsvGGC3Fdht7Mr9ABn5zWpDTZvtgBMjEHLWNJM3x4pPcdwkEg9KTUUVaADNLyfksvkjlOEN8xmi8c6gGY5XVHrMrO1Qo65m3oqQbnUzUq09owtWYKPPWn5VttvrE9a7BD/jmp1ILHRknl+6vBiSKXQyxNmZmUpREgsdT6ZKN/P4tS5kfhYXjro6A5s9uv2caA+u4FaM89C5TQ0hp7YyvV8rwlbmuWb/Ei9dEX50PnDu2S0Sa6aSoSLAZAqSWN50ZIEu8D/PZjScrT8YFQhZ4A+ucGwB3zi7wZI1ZDPlYkWoG+C117TZWxPlRFX/bHaTF/C8hqjqrt8UewG7iN8A9xDfd+ljsFoAAAAASUVORK5CYII=';

export function App() {
  const { session, profile, loading, profileError, isApproved, canApproveUsers, isSales, isPD } = useAuth();
  const [splashStuck, setSplashStuck] = useState(false);

  // Brand the browser tab: title "Liteline" + Liteline favicon.
  useEffect(() => {
    document.title = 'Liteline';
    let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/png';
    link.href = FAVICON_HREF;
  }, []);

  // Watchdog: if we're still on the loading splash after 12 seconds, the
  // app has wedged (typically a corrupted JWT). Surface an Emergency Reset
  // button so the user can fix it themselves instead of clearing cookies.
  useEffect(() => {
    if (!loading) {
      setSplashStuck(false);
      return;
    }
    const t = setTimeout(() => setSplashStuck(true), 12_000);
    return () => clearTimeout(t);
  }, [loading]);

  function emergencyReset() {
    try {
      for (const store of [window.localStorage, window.sessionStorage]) {
        for (let i = store.length - 1; i >= 0; i--) {
          const k = store.key(i);
          if (k && (k.startsWith('sb-') || k === 'liteline.auth')) {
            store.removeItem(k);
          }
        }
      }
    } catch { /* ignore */ }
    window.location.href = '/login';
  }

  if (loading) {
    return (
      <div className="auth-splash" style={{ flexDirection: 'column', gap: 16 }}>
        <div>Loading…</div>
        {splashStuck && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#888', maxWidth: 340, textAlign: 'center', lineHeight: 1.5 }}>
              Still loading? Your saved session may be corrupted. Click below to
              clear it and sign in again.
            </div>
            <button type="button" className="btn btn-out btn-sm" onClick={emergencyReset}>
              🔄 Emergency Reset
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (!profile) {
    return (
      <div className="auth-splash" style={{ flexDirection: 'column', textAlign: 'center', padding: '24px' }}>
        <div style={{ marginBottom: 12 }}>
          {profileError ? `Could not load your profile: ${profileError}` : 'Loading profile…'}
        </div>
        <div style={{ fontSize: 11, color: '#888', maxWidth: 420 }}>
          Signed in as {session.user.email}. If this persists, click Emergency
          Reset below.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-out"
            onClick={() => {
              void supabase.auth.signOut().then(() => { window.location.href = '/login'; });
            }}
          >
            Sign out
          </button>
          <button
            type="button"
            className="btn btn-blue"
            onClick={emergencyReset}
          >
            🔄 Emergency Reset
          </button>
        </div>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <Routes>
        <Route path="*" element={<PendingApprovalPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {!isSales && (
        <Route
          path="samples/scan"
          element={
            <Suspense fallback={<PageLoading />}>
              <SampleScanPage />
            </Suspense>
          }
        />
      )}
      <Route element={<Shell />}>
        <Route index element={isSales ? <Navigate to="/test-requests" replace /> : <HomePage />} />
        {/* Available to everyone (incl. Sales): */}
        <Route path="test-requests/*" element={<RequestTestPage />} />
        <Route path="lab/*" element={<LabPage />} />
        {/* PD + full internal users (not Sales): Sample Control + PMRD. */}
        {!isSales && (
          <>
            <Route path="samples/*" element={<SamplesPage />} />
            <Route path="pmrd/*" element={<PMRDPage />} />
            {/* PD gets read-only access (canWrite is false for PD; DB writes are admin-only). */}
            <Route path="ep/*" element={<EPPage />} />
          </>
        )}
        {/* Full internal users only — hidden from Sales AND Product Development. */}
        {!isSales && !isPD && (
          <>
            <Route path="approvals/*" element={<ApprovalsPage />} />
            <Route path="sample-review/*" element={<SampleReviewPage />} />
            <Route path="quality/*" element={<QualitySystemPage />} />
            <Route path="eng-reports/*" element={<EngineeringReportsPage />} />
            <Route path="cert/*" element={<CertPage />} />
            <Route path="cert-labels/*" element={<CertLabelsPage />} />
            <Route path="equipment/*" element={<EquipmentPage />} />
            <Route path="training/*" element={<TrainingPage />} />
            {canApproveUsers && <Route path="users/*" element={<UsersPage />} />}
          </>
        )}
        <Route path="*" element={<Navigate to={isSales ? '/test-requests' : '/'} replace />} />
      </Route>
    </Routes>
  );
}

// Sign the user out after IDLE_MS of no interaction, leaving a flag so the
// login screen can explain why.
async function performIdleLogout() {
  try { await signOut(); } catch { /* ignore */ }
  try {
    for (const store of [window.localStorage, window.sessionStorage]) {
      for (let i = store.length - 1; i >= 0; i--) {
        const k = store.key(i);
        if (k && (k.startsWith('sb-') || k === 'liteline.auth')) store.removeItem(k);
      }
    }
  } catch { /* ignore */ }
  // Set the reason AFTER clearing, so the clear loop above can't wipe it.
  try { window.sessionStorage.setItem('liteline.idleLogout', '1'); } catch { /* ignore */ }
  window.location.href = '/login';
}

function useIdleLogout() {
  useEffect(() => {
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    // Check once a minute rather than resetting a timer on every event.
    const iv = window.setInterval(() => {
      if (Date.now() - last >= IDLE_MS) {
        window.clearInterval(iv);
        void performIdleLogout();
      }
    }, 60 * 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(iv);
    };
  }, []);
}

/**
 * Run `fn` every `ms` while the tab is visible, and once as soon as it becomes
 * visible again. Background tabs make no requests.
 */
function everyWhileVisible(fn: () => void, ms: number): () => void {
  const iv = window.setInterval(() => { if (document.visibilityState === 'visible') fn(); }, ms);
  const onVis = () => { if (document.visibilityState === 'visible') fn(); };
  document.addEventListener('visibilitychange', onVis);
  return () => { window.clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
}

// Live count of rows in a table matching a status — used to badge nav items
// (new requests, pending approvals, etc.) for the people who action them.
function useStatusCount(table: string, status: string, enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    let alive = true;
    const db = supabase as unknown as { from: (t: string) => any };
    async function load() {
      try {
        // Ask for the number only (no rows downloaded).
        const { count: n } = await db.from(table).select('id', { count: 'exact', head: true }).eq('status', status);
        if (alive) setCount(typeof n === 'number' ? n : 0);
      } catch { /* ignore */ }
    }
    void load();
    const stop = everyWhileVisible(() => { void load(); }, 60 * 1000);
    return () => { alive = false; stop(); };
  }, [table, status, enabled]);
  return count;
}

// Unread updates about MY test requests (received / scheduled / status /
// messages). The database scopes the rows to the signed-in user. Refreshes
// every minute, and immediately when the Lab & Cert Requests page marks some read.
function useUnreadRequestUpdates(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const db = supabase as unknown as { from: (t: string) => any };
    async function load() {
      try {
        const { count: n } = await db.from('request_notifications').select('id', { count: 'exact', head: true }).is('read_at', null);
        if (alive) setCount(typeof n === 'number' ? n : 0);
      } catch { /* not installed yet */ }
    }
    void load();
    const stop = everyWhileVisible(() => { void load(); }, 60 * 1000);
    const onChange = () => { void load(); };
    window.addEventListener('liteline:request-updates', onChange);
    return () => { alive = false; stop(); window.removeEventListener('liteline:request-updates', onChange); };
  }, []);
  return count;
}

function Shell() {
  const {
    email,
    canApproveUsers,
    isAdmin,
    isCoAdmin,
    isTechnician,
    isZidan,
    isPD,
    isSales,
    canWrite,
  } = useAuth();

  // After sign-in, quietly pre-download the most-used modules while the
  // browser is idle, so the first navigation to them is instant.
  useEffect(() => {
    prefetchCommonModules();
  }, []);
  const [showAbout, setShowAbout] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  useIdleLogout();

  // Lab & Cert Requests: notify Lab Tech (technician), admins, co-admin, and Mayu.
  const notifyNewRequests =
    isAdmin || isCoAdmin || isTechnician || email === 'mshan@liteline.com';
  const newReqCount = useStatusCount('test_requests', 'submitted', notifyNewRequests);
  const myRequestUpdates = useUnreadRequestUpdates();
  // PMRD: notify the Certification Manager (admin) and leadership (co-admin).
  const notifyPMRD = isAdmin || isCoAdmin;
  const newPMRDCount = useStatusCount('pmrd_requests', 'submitted', notifyPMRD);
  // Approver reminder: number of users awaiting approval (status 'pending').
  // Approvals: admins, co-admins, or anyone whose role can approve users.
  const notifyApprovals = isAdmin || isCoAdmin || canApproveUsers;
  const pendingApprovals = useStatusCount('users', 'pending', notifyApprovals);
  // Sample Control: admins, co-admins, Sunny (PM) and Zidan (product designer).
  const notifySamples =
    isAdmin ||
    isCoAdmin ||
    email === 'slin@liteline.com' ||
    email === 'zwang@liteline.com';
  const newSampleCount = useStatusCount('samples', 'Pending', notifySamples);

  // Approval requests awaiting MY decision (Pending + I'm listed as an approver).
  const [myApprovalCount, setMyApprovalCount] = useState(0);
  useEffect(() => {
    if (!email) {
      setMyApprovalCount(0);
      return;
    }
    let alive = true;
    const db = supabase as unknown as { from: (t: string) => any };
    const run = () => {
      db.from('approval_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'Pending')
        .contains('approver_emails', [email])
        .then((res: { count: number | null }) => {
          if (alive) setMyApprovalCount(typeof res?.count === 'number' ? res.count : 0);
        });
    };
    run();
    const stop = everyWhileVisible(run, 60000);
    return () => {
      alive = false;
      stop();
    };
  }, [email]);

  async function onSignOut() {
    try {
      await signOut();
    } catch (e) {
      console.warn('[signOut]', e);
    }
    // Belt-and-braces: clear any persisted Supabase session keys that the
    // remember-me toggle may have moved between localStorage/sessionStorage.
    try {
      for (const store of [window.localStorage, window.sessionStorage]) {
        for (let i = store.length - 1; i >= 0; i--) {
          const k = store.key(i);
          if (k && (k.startsWith('sb-') || k === 'liteline.auth')) {
            store.removeItem(k);
          }
        }
      }
    } catch {
      /* ignore */
    }
    // Hard reload to /login so React state fully resets (no stale auth context).
    window.location.href = '/login';
  }

  const role = isAdmin
    ? 'Admin'
    : isCoAdmin
      ? 'Co-Admin'
      : isSales
        ? 'Sales'
        : isTechnician
          ? 'Technician'
          : isZidan
            ? 'Designer'
            : isPD
              ? 'Product Development'
              : 'Engineer';

  return (
    <>
      <div className="sidebar">
        <div className="sb-logo">
          <LitelineLogo />
        </div>

        {isSales ? (
          // Sales: restricted to Lab & Cert Requests + Lab (view-only).
          <>
            <div className="sb-sec">Menu</div>
            <SbiLink to="/test-requests" icon="🧪" label="Lab & Cert Requests" badge={myRequestUpdates} />
            <SbiLink to="/lab" icon="🔬" label="Lab Schedule - Engineering" />
          </>
        ) : isPD ? (
          // Product Development: Home, Lab & Cert Requests, Lab, Sample Control, PMRD.
          <>
            <div className="sb-sec">Main</div>
            <SbiLink to="/" icon="⌂" label="Home" end />
            <div className="sb-sec">Modules</div>
            <SbiLink to="/test-requests" icon="🧪" label="Lab & Cert Requests" badge={myRequestUpdates} />
            <SbiLink to="/lab" icon="🔬" label="Lab Schedule - Engineering" />
            <SbiLink to="/ep" icon="📋" label="Engineering Projects" />
            <SbiLink to="/samples" icon="📊" label="Sample Control" badge={newSampleCount} />
            <SbiLink to="/pmrd" icon="🏷️" label="PMRD" />
          </>
        ) : (
          <>
            <div className="sb-sec">Main</div>
            <SbiLink to="/" icon="⌂" label="Home" end />
            <SbiLink to="/approvals" icon="✓" label="Approvals" badge={myApprovalCount} />

            <div className="sb-sec">Modules</div>
            <SbiLink to="/test-requests" icon="🧪" label="Lab & Cert Requests" badge={newReqCount + myRequestUpdates} />
            <SbiLink to="/ep" icon="📋" label="Engineering Projects" />
            <SbiLink to="/lab" icon="🔬" label="Lab Schedule - Engineering" />
            <SbiLink to="/samples" icon="📊" label="Sample Control" badge={newSampleCount} />
            <SbiLink to="/sample-review" icon="🔬" label="Mechanical Reviews & RMAs" />
            <SbiLink to="/quality" icon="📘" label="Quality System" />
            <SbiLink to="/pmrd" icon="🏷️" label="PMRD" badge={newPMRDCount} />
            <SbiLink to="/eng-reports" icon="📑" label="Engineering Reports" />
            <SbiLink to="/cert" icon="🏆" label="Certification Tracker" />
            <SbiLink to="/cert-labels" icon="🏷️" label="Certification Labels" />
            <SbiLink to="/equipment" icon="⚙️" label="Engineering Equipment" />
            <SbiLink to="/training" icon="🎓" label="Training" />

            {canApproveUsers && (
              <>
                <div className="sb-sec">Settings</div>
                <SbiLink to="/users" icon="👤" label="Users & Admin" badge={pendingApprovals} />
              </>
            )}
          </>
        )}

        {/* Footer: user identity + account actions, evenly stacked. */}
        <div className="sb-user" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div className="av">{emailInitials(email)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {email}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)' }}>
                {role}
                {!canWrite && ' · read-only'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              style={sideBtn}
              onClick={() => setShowChangePw(true)}
              title="Change your password"
            >
              🔑&nbsp;&nbsp;Change Password
            </button>
          </div>
        </div>
      </div>

      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}

      <div className="main">
        <div className="topbar">
          <span
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              flex: 1,
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ color: 'var(--text)' }}>Engineering Portal</span>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowAbout(true)}
              title="About this portal"
              style={topBtn}
            >
              About
            </button>
            <button
              type="button"
              onClick={onSignOut}
              title="Sign out"
              style={signOutBtn}
            >
              Sign Out
            </button>
          </div>
        </div>
        <div className="content">
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </>
  );
}

// Topbar action buttons — quiet, outlined, consistent height.
const topBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--muted)',
  padding: '5px 12px',
  borderRadius: 7,
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const signOutBtn: CSSProperties = {
  ...topBtn,
  borderColor: 'rgba(220,38,38,.35)',
  color: '#dc2626',
};

// Sidebar footer buttons — full width, even, subtle on the dark sidebar.
const sideBtn: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,.08)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,.14)',
  padding: '8px 12px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center',
  fontFamily: 'inherit',
};

function SbiLink({
  to,
  icon,
  label,
  end,
  badge,
}: {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'sbi active' : 'sbi')}>
      <span className="ico">{icon}</span>
      {label}
      {badge ? <span style={navBadge}>{badge}</span> : null}
    </NavLink>
  );
}

const navBadge: CSSProperties = {
  marginLeft: 'auto',
  background: '#dc2626',
  color: '#fff',
  fontSize: 10,
  fontWeight: 800,
  borderRadius: 999,
  padding: '1px 7px',
  minWidth: 18,
  textAlign: 'center',
};

function LitelineLogo() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: '#ffffff',
        borderRadius: 10,
        padding: '7px 12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
      }}
    >
      <img src={LOGO_HREF} alt="Liteline" style={{ height: 26, display: 'block' }} />
    </span>
  );
}
